import React from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PAGE_TITLE_KEYS, TITLE_BRAND } from '@config/pageTitles.ts';
import { usePageTitleStore } from '@hooks/usePageTitle.ts';

/**
 * Jediné místo, které plní `<title>`. React 19 si tag z komponenty vytáhne do hlavičky a
 * přitom recykluje `<title>` z `index.html` (nehledá se nový, bere `getElementsByTagName`),
 * takže se tagy nezdvojí. Renderovat ho jinde už ale nemá smysl — proto jen tady, v rootu.
 *
 * Titulek bere z `usePageTitle` (dynamický) nebo z registru podle id nejhlubší matchnuté
 * routy. i18n je přes hook, aby se titulek přeložil i po přepnutí jazyka; `useSuspense: false`
 * proto, že suspendovat kvůli titulku celý root by pozdrželo celou aplikaci — dokud se
 * překlady nedotáhnou, zobrazí se jen značka.
 */
const PageTitle: React.FC = () => {
  const { t, ready } = useTranslation(['admin', 'client'], { useSuspense: false });
  const override = usePageTitleStore(s => s.title);
  // Nejhlubsi matchnuta routa = ta, co stranku skutecne renderuje. Index misto `at(-1)`,
  // protoze `lib` projektu je ES2020 a `Array.prototype.at` tam jeste neni typovane.
  const routeId = useRouterState({ select: s => s.matches[s.matches.length - 1]?.routeId });

  const staticKey = routeId ? PAGE_TITLE_KEYS[routeId as keyof typeof PAGE_TITLE_KEYS] : undefined;
  // Bez `ready` by `t()` vrátilo samotný klíč a v tabu by chvíli svítilo "pageTitle.search".
  const pageTitle = override || (ready && staticKey ? t(staticKey) : undefined);

  if (import.meta.env.DEV && ready && !pageTitle && routeId) {
    console.warn(`[PageTitle] Routa ${routeId} nemá titulek — dopiš ji do PAGE_TITLE_KEYS, nebo zavolej usePageTitle().`);
  }

  return <title>{pageTitle ? `${pageTitle} | ${TITLE_BRAND}` : TITLE_BRAND}</title>;
};

export default PageTitle;
